import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowsClockwise, PersonSimpleRun, Bicycle, Sparkle, SmileySad, HourglassHigh,
  MagicWand, CalendarBlank,
} from '@phosphor-icons/react';
import { WeeklyPlanRenderer } from '../components/WeeklyPlanRenderer';
import { Header, Card, Button, Skeleton, EmptyState } from '../components/ui';
import authFetch from '../utils/authFetch';
import { planRegenRequest } from '../utils/planRegenRequest';
import { runAutoRegen, resetSlots, slotKey } from '../utils/autoPlanRegen';
import { showToast } from '../utils/toast';
import { CYCLING_ENABLED } from '../config/features';
import PlanRebuildStatus from '../components/plan/PlanRebuildStatus';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

// Detect which plan format is in DB. v2 plans have `days` array per schema;
// v1 plans have `plan_text` string. Backwards-compat reader — old plans
// keep rendering through the markdown formatter, new ones use the
// WeeklyPlanRenderer component.
// The four visible week slots, in order (the Skeleton Master shows 4 weeks ahead).
const WEEK_SLOTS = ['current', 'next', 'week_plus2', 'week_plus3'];
// Goal race distance → Hebrew; an unknown value is hidden rather than shown raw.
function raceDistanceLabel(v) {
  const k = String(v ?? '').trim().toLowerCase().replace(/\s+/g, '').replace(/k$|km$|ק"מ$|ק״מ$/, '');
  return ({ '5': '5 ק״מ', '10': '10 ק״מ', '21.1': 'חצי מרתון', '21': 'חצי מרתון', 'half_marathon': 'חצי מרתון',
            'half': 'חצי מרתון', 'חצי': 'חצי מרתון', 'חצימרתון': 'חצי מרתון', '42.2': 'מרתון', '42': 'מרתון',
            'marathon': 'מרתון', 'מרתון': 'מרתון' })[k] || null;
}

function isV2Plan(workoutsJson) {
  return (
    workoutsJson
    && typeof workoutsJson === 'object'
    && Array.isArray(workoutsJson.days)
    && workoutsJson.days.length === 7
  );
}

function WeeklyPlan({ onBack, userId, onPlanGenerated, isAdmin = false }) {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [userSports, setUserSports] = useState(['running']); // loaded from profile
  const [sportTab, setSportTab] = useState('running');
  const [weekTab, setWeekTab] = useState('current');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // States for generating plan
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [limitData, setLimitData] = useState(null); // { dailyLimit, trialDaysLeft }
  const [countdown, setCountdown] = useState('');

  // ── State 2 (background sync) ────────────────────────────────────
  // bgSync.tasks[] entries: { sport, week, status: 'running'|'done'|'error' }
  // Renders a single 'syncing with coach' banner whenever activeCount>0,
  // and a transient toast when the 'next' week finishes.
  const [bgSync, setBgSync] = useState({ tasks: [], activeCount: 0 });
  const [toast, setToast] = useState(null); // string | null — auto-dismiss after 3s
  // Guard: detection runs once per mount. Prevents a re-fetch loop where
  // each successful background generation reloads plans → useEffect
  // sees new plans array → tries to detect missing again.
  const bgSyncStartedRef = useRef(false);
  // P0 2026-10-18: weeks that could not be generated automatically (permanent failure / retries exhausted) —
  // shown with the reason and a manual retry. Recomputed from utils/autoPlanRegen on every mount, so a remount
  // never hides it and never re-fires the generation.
  const [autoRegenBlocked, setAutoRegenBlocked] = useState([]);
  const [manualRetrying, setManualRetrying] = useState(false);

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      const diff = Math.max(0, midnight - now);
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const getCurrentWeekStart = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day;
    const sunday = new Date(today);
    sunday.setDate(diff);
    return sunday.toISOString().split('T')[0];
  };

  const getNextWeekStart = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + 7;
    const nextSunday = new Date(today);
    nextSunday.setDate(diff);
    return nextSunday.toISOString().split('T')[0];
  };

  const getWeekStartByOffset = (n) => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (n * 7);
    const d = new Date(today);
    d.setDate(diff);
    return d.toISOString().split('T')[0];
  };

  const getWeekSunday = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    const day = date.getDay();
    const diff = date.getDate() - day;
    const sunday = new Date(date);
    sunday.setDate(diff);
    return sunday.toISOString().split('T')[0];
  };

  const categorizePlans = (allPlans) => {
    const currentWeekSunday = getCurrentWeekStart();
    const nextWeekSunday = getNextWeekStart();
    const week2Sunday = getWeekStartByOffset(2);
    const week3Sunday = getWeekStartByOffset(3);

    const categorized = {
      running: { current: null, next: null, week_plus2: null, week_plus3: null },
      cycling: { current: null, next: null, week_plus2: null, week_plus3: null },
    };

    for (const plan of allPlans) {
      const planWeekSunday = getWeekSunday(plan.week_start_date);
      const planSportType = plan.sport_type || 'running';
      const sportKey = planSportType === 'cycling' ? 'cycling' : 'running';

      if (planWeekSunday === currentWeekSunday) {
        categorized[sportKey].current = plan;
      } else if (planWeekSunday === nextWeekSunday) {
        categorized[sportKey].next = plan;
      } else if (planWeekSunday === week2Sunday) {
        categorized[sportKey].week_plus2 = plan;
      } else if (planWeekSunday === week3Sunday) {
        categorized[sportKey].week_plus3 = plan;
      }
    }

    return categorized;
  };

  const fetchAllPlans = async (suppressError = false, targetSport = null, targetWeek = null, knownSports = null, silent = false) => {
    if (!silent) setIsLoading(true);
    if (!suppressError) setError(null);

    // Resilience to backend deploys: hydrate plans from localStorage
    // BEFORE the fetch fires, so the user always sees their last-known
    // dashboard instead of an empty screen while Render flips
    // instances. Cache is overwritten on every successful response.
    if (userId) {
      try {
        const cached = localStorage.getItem(`plans_${userId}`);
        if (cached) {
          const data = JSON.parse(cached);
          if (Array.isArray(data) && data.length > 0) {
            // Filter out plans for sports the athlete no longer trains
            // (e.g. removed running, only cycling now). Without this
            // filter the old running plan flashes on screen before the
            // fresh fetch arrives — user 2026-06-10 report.
            const _activeSet = new Set(
              knownSports || userSports || ['running', 'cycling']
            );
            const _filtered = data.filter(p => {
              const _ps = p.sport_type || 'running';
              return _activeSet.has(_ps);
            });
            setPlans(_filtered);
            // Purge stale entries from cache too so the next mount stays
            // clean. We rewrite immediately rather than waiting for fetch.
            if (_filtered.length !== data.length) {
              localStorage.setItem(`plans_${userId}`, JSON.stringify(_filtered));
            }
          }
        }
      } catch {}
    }

    // Render free tier wakes from sleep in ~30-60s. 3 retries × 3s linear
    // backoff (max 9s) wasn't enough — users hit "Failed to fetch" during
    // cold start. Bumped to 5 retries with exponential backoff (2s, 4s,
    // 8s, 16s, 32s = up to ~62s total), which covers the worst-case wake.
    const MAX_RETRIES = 5;
    let lastErr = null;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) {
          const waitMs = Math.min(2000 * Math.pow(2, attempt - 1), 32000);
          await new Promise(resolve => setTimeout(resolve, waitMs));
          console.log(`Retrying weekly plans fetch (attempt ${attempt + 1}/${MAX_RETRIES}) after ${waitMs}ms...`);
        }

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 35000);
        let response;
        try {
          response = await authFetch(`${API_BASE_URL}/weekly-plan/all-active?user_id=${userId}`, { signal: controller.signal });
        } finally {
          clearTimeout(timeout);
        }

        if (response.status === 404) {
          setPlans([]);
          setSelectedPlan(null);
          setIsLoading(false);
          return;
        }

        if (!response.ok) throw new Error('Failed to fetch plans');

        const data = await response.json();
        const _rawPlans = data.plans || [];
        // Filter by activeSports so plans for sports the athlete dropped
        // (e.g. removed running, kept only cycling) don't leak into UI.
        // Backend keeps them in DB for history, but the UI must reflect
        // the CURRENT sports_active state.
        const _activeSet = new Set(
          knownSports || userSports || ['running', 'cycling']
        );
        const allPlans = _rawPlans.filter(p => {
          const _ps = p.sport_type || 'running';
          return _activeSet.has(_ps);
        });
        // Persist for the next load — see hydration at the top of
        // fetchAllPlans. Best-effort; localStorage can throw on quota.
        try { localStorage.setItem(`plans_${userId}`, JSON.stringify(allPlans)); } catch {}
        setPlans(allPlans);

        const categorized = categorizePlans(allPlans);
        const useSport = targetSport || sportTab;
        const useWeek = targetWeek || weekTab;

        const targetPlan = categorized[useSport]?.[useWeek];
        if (targetPlan) {
          setSelectedPlan(targetPlan);
          if (targetSport) setSportTab(targetSport);
          if (targetWeek) setWeekTab(targetWeek);
        } else {
          // Auto-select: use knownSports (passed in) or fall back to userSports state
          const effectiveSports = knownSports || userSports;
          const preferCycling = !effectiveSports.includes('running') && effectiveSports.includes('cycling');
          const firstSport = preferCycling ? 'cycling' : 'running';
          const secondSport = preferCycling ? 'running' : 'cycling';
          if (categorized[firstSport].current) {
            setSelectedPlan(categorized[firstSport].current);
            setSportTab(firstSport);
            setWeekTab('current');
          } else if (categorized[firstSport].next) {
            setSelectedPlan(categorized[firstSport].next);
            setSportTab(firstSport);
            setWeekTab('next');
          } else if (effectiveSports.includes(secondSport) && categorized[secondSport].current) {
            // Only fall back to second sport if user actually has that sport
            setSelectedPlan(categorized[secondSport].current);
            setSportTab(secondSport);
            setWeekTab('current');
          } else if (effectiveSports.includes(secondSport) && categorized[secondSport].next) {
            setSelectedPlan(categorized[secondSport].next);
            setSportTab(secondSport);
            setWeekTab('next');
          } else {
            setSelectedPlan(null);
          }
        }

        setError(null);
        setIsLoading(false);
        return;

      } catch (err) {
        console.error(`Fetch attempt ${attempt + 1} failed:`, err);
        lastErr = err;
      }
    }

    // All retries exhausted
    if (!suppressError) {
      setError('לא הצלחתי לטעון את התכניות');
    }
    setIsLoading(false);
  };

  // 🆕 Generate plan function — uses the dual-mode planRegenRequest
  // helper so it transparently handles both the legacy sync response
  // (200 + full plan) and the new async response (202 + job_id +
  // polling). plan-regen-start/done events fire automatically inside
  // the helper, so we don't dispatch them here.
  const generatePlan = async (weekType, sportType, overwriteManual = false) => {
    const sportHe = sportType === 'cycling' ? 'רכיבה' : 'ריצה';
    const weekHe = weekType === 'current' ? 'שבוע נוכחי' : 'שבוע הבא';
    try {
      setIsGenerating(true);
      setGenerateError(null);

      console.log(`🚀 Generating ${sportType} plan for ${weekType} week...`);
      const result = await planRegenRequest({
        url: `${API_BASE_URL}/weekly-plan/generate-v2?user_id=${userId}`,
        body: { week_type: weekType, sport_type: sportType,
                ...(overwriteManual ? { overwrite_manual: true } : {}) },
        pollUserId: userId,
        changedFields: [`תכנית ${sportHe} — ${weekHe}`],
      });

      // EDITED-WEEK PROTECTION (2026-08-28): the backend preserved a week
      // that carries manual/chat edits. Overwriting requires an explicit
      // user confirmation — Cancel means zero mutation.
      const _preserved = result?.result_meta?.preserved || result?.preserved;
      if (result.success && _preserved && !overwriteManual) {
        setIsGenerating(false);
        const ok = window.confirm(
          'השבוע כולל שינויים שביצעת ידנית או דרך המאמן. ' +
          'יצירת התכנית מחדש תמחק אותם. להמשיך?'
        );
        if (ok) return generatePlan(weekType, sportType, true);
        showToast('השינויים שלך נשמרו — התכנית לא שונתה');
        return;
      }

      if (!result.success) {
        // Same soft-error branches as before — limit / no_profile /
        // blocked / generic. The helper passes data through.
        if (result.error === 'limit_reached') {
          setGenerateError('limit_reached');
          try {
            const usageRes = await authFetch(`${API_BASE_URL}/usage/${userId}`);
            const usage = await usageRes.json();
            setLimitData({ dailyLimit: usage.daily_limit, trialDaysLeft: usage.trial_days_left || 0 });
          } catch {
            setLimitData({ dailyLimit: result.daily_limit, trialDaysLeft: result.trial_days_left || 0 });
          }
        } else if (result.error === 'no_profile') {
          setGenerateError('אנא מלא את הפרופיל שלך קודם');
        } else if (result.error === 'blocked') {
          setGenerateError('החשבון שלך ממתין לאישור');
        } else if (result.error === 'polling_timeout') {
          // The BG worker may have saved the plan before its container
          // restarted — the rescue path in planRegenRequest checks
          // active_plans before we get here, so reaching this branch
          // means there really isn't a fresh plan yet. Refetch to be
          // safe and ask the user to look at the dashboard.
          fetchAllPlans(true);
          setGenerateError('הבקשה לקחה זמן רב מהצפוי. בדוק את התכנית השבועית — ייתכן שהיא כבר מוכנה. אם לא, נסה שוב בעוד דקה.');
        } else {
          setGenerateError(result.message || result.error || 'שגיאה ביצירת התכנית');
        }
        return;
      }

      console.log('✅ Plan generated successfully!', result);

      // Explicit success toast — the global plan-regen banner only
      // stays green for 4s and can be missed if the user navigated
      // away or backgrounded the tab. Toast persists 3.5s in the
      // bottom-center and is harder to miss. User feedback 2026-06-02:
      // "no confirmation that the plan was created" → add this.
      const _sportHe = sportType === 'cycling' ? 'רכיבה' : 'ריצה';
      const _weekHe = weekType === 'current' ? 'השבוע' : 'השבוע הבא';
      showToast(`✅ תכנית ${_sportHe} ל${_weekHe} נוצרה בהצלחה!`);

      // DB-write commit lag: even on the async path, the row was
      // committed before mark_completed fired, so 1s is enough now.
      // Keeping 4s for sync responses to be safe (race on Render).
      await new Promise(resolve => setTimeout(resolve, 4000));
      const targetWeek = weekType === 'current' ? 'current' : 'next';
      await fetchAllPlans(true, sportType, targetWeek);
    } catch (err) {
      console.error('Error generating plan:', err);
      if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
        setGenerateError('השרת לא הגיב. אם זה קרה לאחר עדכון, נסה שוב בעוד 30 שניות.');
      } else {
        setGenerateError(err.message || 'שגיאה ביצירת התכנית. נסה שוב.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    // Load sports FIRST, then fetch plans — prevents race condition where
    // fetchAllPlans auto-selects wrong sport before userSports is known
    const init = async () => {
      let activeSports = ['running'];
      try {
        const res = await authFetch(`${API_BASE_URL}/profile/cycling/${userId}`);
        if (res.ok) {
          const cp = await res.json();
          if (cp?.sports_active) {
            activeSports = cp.sports_active.split(',').map(s => s.trim()).filter(Boolean);
            setUserSports(activeSports);
            if (activeSports.includes('cycling') && !activeSports.includes('running')) {
              setSportTab('cycling');
            }
          }
        }
      } catch {}
      // Fetch plans only after sports are known — pass them directly to avoid stale closure
      await fetchAllPlans(false, null, null, activeSports);
    };
    init();

    const handlePlansUpdated = (event) => {
      console.log('🔄 WeeklyPlan: plansUpdated event received!', event.detail);
      // Delay to allow DB write to complete before fetching
      setTimeout(() => fetchAllPlans(), 2000);
      
      if (event.detail) {
        const { sportType, weekStart } = event.detail;
        const nextWeekSunday = getNextWeekStart();
        
        if (sportType) {
          setSportTab(sportType);
        }
        if (weekStart === nextWeekSunday) {
          setWeekTab('next');
        }
      }
    };
    
    window.addEventListener('plansUpdated', handlePlansUpdated);
    
    return () => {
      window.removeEventListener('plansUpdated', handlePlansUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // ── State 2: lazy auto-check for missing weekly plans ─────────────
  // After the initial load (isLoading flips false) we know which plans
  // the DB has. For each active sport we check 'current' and 'next' —
  // anything missing gets fired in the background via the V2 generate
  // endpoint. The user sees a non-blocking 'syncing with coach' banner
  // up top; when a 'next week' plan finishes a toast tells them so
  // they don't have to look at the tab to know.
  //
  // Guardrails:
  //   - bgSyncStartedRef ensures detection runs exactly once per mount.
  //     fetchAllPlans() refreshes plans[] on success, which would
  //     otherwise re-fire this useEffect indefinitely.
  //   - Skipped when userSports is empty (profile not loaded yet).
  //   - Skipped for admin-disabled fallback users? No — auto-check is
  //     the whole point; the admin gate only affects the manual button.
  useEffect(() => {
    if (isLoading) return;             // wait for initial fetch
    if (bgSyncStartedRef.current) return;
    if (!userId) return;
    if (!userSports || userSports.length === 0) return;

    const cat = categorizePlans(plans);
    // Check all 4 week slots. Skeleton generation is free (no LLM). A slot is (sport, week START DATE) — the
    // coordinator (utils/autoPlanRegen) decides whether it may be generated automatically: never twice at once,
    // never again after a permanent failure (plan_frozen / persist_failed / not_persisted), transient failures
    // with bounded backoff, success only when the plan is read back. Its state survives remounts and reloads —
    // App.js remounts this page on every App state change (P0 18.10: a job every ~9 s while week +3 was missing).
    const missing = missingSlots(cat);
    if (missing.length === 0) { setAutoRegenBlocked([]); return; }

    // Skip if a global regenerate-all batch is already in flight (Onboarding Step6 / Profile save).
    const _globalRegenInFlight = (() => {
      try {
        return !!window.__planRegenInFlight;
      } catch { return false; }
    })();
    if (_globalRegenInFlight) {
      console.log(`🔄 [State2] skip bgSync — global regenerate-all batch already in flight`);
      return;
    }

    bgSyncStartedRef.current = true;
    (async () => {
      const res = await runAutoRegen({ userId, slots: missing, request: requestSlots, verify: verifySlots });
      afterAutoRegen(res);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, userSports]);

  // ── Automatic generation helpers (P0 2026-10-18, utils/autoPlanRegen) ──
  const WEEK_LABEL = (w) =>
    w === 'next' ? 'שבוע הבא' : w === 'week_plus2' ? 'שבוע +2' : w === 'week_plus3' ? 'שבוע +3' : 'שבוע נוכחי';
  const slotWeekStart = (week) => ({
    current: getCurrentWeekStart(), next: getNextWeekStart(),
    week_plus2: getWeekStartByOffset(2), week_plus3: getWeekStartByOffset(3),
  })[week];
  function missingSlots(cat) {
    const out = [];
    for (const sport of userSports) {
      for (const week of ['current', 'next', 'week_plus2', 'week_plus3']) {
        if (!cat[sport]?.[week]) out.push({ sport, week, weekStart: slotWeekStart(week) });
      }
    }
    return out;
  }
  async function requestSlots(todo, manual = false) {
    setBgSync({ tasks: todo.map(t => ({ ...t, status: 'running' })), activeCount: todo.length });
    try { window.__planRegenInFlight = true; } catch {}
    window.dispatchEvent(new CustomEvent('plan-regen-start', {
      detail: { changedFields: todo.map(m => `תכנית ${m.sport === 'cycling' ? 'רכיבה' : 'ריצה'} — ${WEEK_LABEL(m.week)}`) },
    }));
    try {
      return await planRegenRequest({
        url: `${API_BASE_URL}/weekly-plan/regenerate-all?user_id=${userId}`,
        body: { sports: [...new Set(todo.map(m => m.sport))], weeks: [...new Set(todo.map(m => m.week))],
                triggered_by_user: false, force: false, ...(manual ? { manual_retry: true } : {}) },
        pollUserId: userId,
        silent: true,
        maxPollMs: 8 * 60 * 1000,
      });
    } finally {
      try { window.__planRegenInFlight = false; } catch {}
    }
  }
  // Success is what the server HAS: read the active plans back and match each slot by sport + week start.
  async function verifySlots() {
    const res = await authFetch(`${API_BASE_URL}/weekly-plan/all-active?user_id=${encodeURIComponent(userId)}`);
    if (!res.ok) return new Set();
    const data = await res.json();
    return new Set(((data && data.plans) || []).map(p =>
      slotKey((p.sport_type || 'running') === 'cycling' ? 'cycling' : 'running', getWeekSunday(p.week_start_date))));
  }
  async function afterAutoRegen(res) {
    setAutoRegenBlocked(res.blocked || []);
    if (!res.ran) return;                       // nothing fired: no banner, no events, no App re-render
    const ok = res.outcome === 'succeeded';
    setBgSync({ tasks: res.slots.map(t => ({ ...t, status: ok ? 'done' : 'error' })), activeCount: 0 });
    if (ok) {
      await fetchAllPlans(true, null, null, null, true);
      const t = res.slots.find(x => x.week !== 'current');
      if (t) setToast(`תכנית ${t.week === 'next' ? 'השבוע הבא' : t.week === 'week_plus2' ? '+2 שבועות' : '+3 שבועות'} ${t.sport === 'cycling' ? 'ברכיבה' : 'בריצה'} מוכנה! 📅`);
    }
    window.dispatchEvent(new CustomEvent('plan-regen-done', { detail: { success: ok } }));
  }
  async function manualRetry() {
    const slots = autoRegenBlocked.map(({ sport, week, weekStart }) => ({ sport, week, weekStart }));
    if (!slots.length || manualRetrying) return;
    setManualRetrying(true);
    try {
      resetSlots(userId, slots);
      const res = await runAutoRegen({ userId, slots, request: (t) => requestSlots(t, true), verify: verifySlots,
                                       manual: true });
      await afterAutoRegen(res);
    } finally {
      setManualRetrying(false);
    }
  }
  const BLOCK_REASON = (code) =>
    code === 'plan_frozen' ? 'התכנית מוקפאת (מצב בדיקה)'
      : (code === 'persist_failed' || code === 'not_persisted') ? 'התכנית נבנתה אבל לא נשמרה'
      : (code === 'unauthorized' || code === 'forbidden' || code === 'blocked') ? 'אין הרשאה ליצור תכנית'
      : 'שגיאה זמנית — הניסיונות האוטומטיים הסתיימו';

  // Auto-dismiss toast after 3 seconds, per the State 2 spec
  // ('transient ... disappears after 3 seconds without interrupting').
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const categorizedPlans = categorizePlans(plans);
  const currentSportPlans = categorizedPlans[sportTab] || { current: null, next: null, week_plus2: null, week_plus3: null };

  const handleSportTabChange = (sport) => {
    setSportTab(sport);
    setGenerateError(null);
    const sportPlans = categorizedPlans[sport];
    
    if (weekTab === 'current' && sportPlans.current) {
      setSelectedPlan(sportPlans.current);
    } else if (weekTab === 'next' && sportPlans.next) {
      setSelectedPlan(sportPlans.next);
    } else if (sportPlans.current) {
      setSelectedPlan(sportPlans.current);
      setWeekTab('current');
    } else if (sportPlans.next) {
      setSelectedPlan(sportPlans.next);
      setWeekTab('next');
    } else {
      setSelectedPlan(null);
    }
  };

  const handleWeekTabChange = (week) => {
    setWeekTab(week);
    setGenerateError(null);
    const plan = currentSportPlans[week];
    setSelectedPlan(plan);
  };

  const formatWeekDisplay = (dateStr) => {
    if (!dateStr) return '';
    const weekSunday = getWeekSunday(dateStr);
    const sunday = new Date(weekSunday + 'T12:00:00');
    const saturday = new Date(sunday);
    saturday.setDate(saturday.getDate() + 6);

    const formatDate = (d) => `${d.getDate()}/${d.getMonth() + 1}`;
    return `${formatDate(sunday)} - ${formatDate(saturday)}`;
  };

  // 🎨 Format plan text with highlighted day headers
  const formatPlanText = (planText, isCycling = false) => {
    if (!planText) return null;

    const lines = planText.split('\n');
    const elements = [];

    // Patterns for day headers
    const dayHeaderPatterns = [
      /^#{1,3}\s*(ראשון|שני|שלישי|רביעי|חמישי|שישי|שבת)\s*\|\s*(\d{1,2}\.\d{1,2}\.\d{4})/,  // # / ## / ### ראשון | 22.02.2026
      /^#{1,3}\s*יום\s*(ראשון|שני|שלישי|רביעי|חמישי|שישי|שבת)\s*\((\d{1,2}[./]\d{1,2})/,    // # / ### יום ראשון (22/2) or (22.5)
      /^#{1,3}\s*(ראשון|שני|שלישי|רביעי|חמישי|שישי|שבת)\s*\((\d{1,2}[./]\d{1,2})/,          // # / ### ראשון (22/2)
      /^#{1,3}\s*יום\s*(ראשון|שני|שלישי|רביעי|חמישי|שישי|שבת)\s*[(\s]/,                      // # יום ראשון (no date)
      /^##\s*🗓️?\s*(ראשון|שני|שלישי|רביעי|חמישי|שישי|שבת)/,                                 // ## 🗓️ ראשון
    ];

    const dayEmojis = {
      'ראשון': '☀️',
      'שני': '💪',
      'שלישי': '🔥',
      'רביעי': '⚡',
      'חמישי': '🎯',
      'שישי': '🏃',
      'שבת': '😴'
    };

    lines.forEach((line, index) => {
      let isDayHeader = false;
      let dayName = '';
      let dateStr = '';

      for (const pattern of dayHeaderPatterns) {
        const match = line.match(pattern);
        if (match) {
          isDayHeader = true;
          dayName = match[1];
          dateStr = match[2] || '';
          break;
        }
      }

      if (isDayHeader) {
        // Extract title after the date (if exists)
        const titleMatch = line.match(/[-–]\s*(.+)$/);
        const title = titleMatch ? titleMatch[1].trim() : '';

        const emoji = dayEmojis[dayName] || '📅';
        const bgColor = isCycling ? ' ' : ' ';
        const borderColor = isCycling ? 'border-border' : 'border-border';

        elements.push(
          <div
            key={index}
            className={`my-4 p-4 rounded-xl bg-surface ${bgColor} border ${borderColor} `}
          >
            <div className="flex items-center gap-3">
              <span className="text-3xl">{emoji}</span>
              <div>
                <div className="text-xl font-bold text-text">
                  יום {dayName}
                  {dateStr && <span className="text-text-body font-normal mr-2">| {dateStr}</span>}
                </div>
                {title && (
                  <div className="text-lg text-text-body font-medium mt-1">
                    {title}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      } else if (line.trim()) {
        // Check for section headers (## 🎯 מטרת השבוע, ## 📊 סיכום שבועי)
        const sectionMatch = line.match(/^##\s*(.+)$/);
        if (sectionMatch) {
          const sectionTitle = sectionMatch[1].trim();
          const bgColor = isCycling ? ' ' : ' ';
          const borderColor = isCycling ? 'border-border' : 'border-border';
          const textColor = isCycling ? 'text-text-body' : 'text-text-body';

          elements.push(
            <div
              key={index}
              className={`my-4 p-3 rounded-xl bg-surface ${bgColor} border ${borderColor}`}
            >
              <div className={`text-lg font-bold ${textColor}`}>
                {sectionTitle}
              </div>
            </div>
          );
          return;
        }

        // Regular text line - check for special formatting
        let formattedLine = line;

        // Bold text with ** or __
        formattedLine = formattedLine.replace(/\*\*(.+?)\*\*/g, '<strong class="text-text font-semibold">$1</strong>');

        // Check if it's a workout type line (contains specific keywords)
        const isWorkoutType = /^(🏃|🚴|💪|🎯|📏|⏱️|❤️|📝|✅|-\s)/.test(line.trim());

        elements.push(
          <div
            key={index}
            className={`${isWorkoutType ? 'mt-2' : ''}`}
            dangerouslySetInnerHTML={{ __html: formattedLine }}
          />
        );
      } else {
        // Empty line
        elements.push(<div key={index} className="h-2" />);
      }
    });

    return elements;
  };

  const plan = selectedPlan;

  // Header subtitle (§5.2: "חצי מרתון · שבוע 4 מתוך 8") — read directly off fields the rest of
  // this file already trusts (week_number/total_weeks are top-level DB-row fields exactly like
  // the ones WeeklyPlanRenderer reads off the same row; race_distance is read the same way
  // calculateTotalKm above reads other workouts_json fields). Falls back to the sport name alone
  // when a field is missing — never invents a week count or distance that isn't there.
  // Graphite (2026-10-27): "<phase> · <race distance> · שבוע X מתוך 4" — X is the selected one of the four visible
  // weeks. A part the plan row does not carry is left out, never invented.
  const headerSubtitle = (() => {
    const fallback = sportTab === 'cycling' ? 'רכיבה' : 'ריצה';
    if (!plan) return fallback;
    const wj = isV2Plan(plan.workouts_json) ? plan.workouts_json : {};
    const phase = typeof wj.phase_label === 'string' ? wj.phase_label.replace(/\s*\([A-Za-z ]+\)\s*$/, '').trim() : '';
    const raceDist = raceDistanceLabel(wj.race_distance || wj.context?.goal_race?.distance);
    const idx = WEEK_SLOTS.indexOf(weekTab);
    const weekPart = idx >= 0 ? `שבוע ${idx + 1} מתוך ${WEEK_SLOTS.length}` : null;
    return [phase, raceDist, weekPart].filter(Boolean).join(' · ') || fallback;
  })();

  // For cycling, time is the planning unit, not km. Compute the
  // weekly duration (sum of day.duration_min) from the V2 JSON when
  // available. Falls back to the plan's estimated_duration_minutes.
  const computeWeeklyDurationMin = (p) => {
    if (!p) return null;
    const wj = p.workouts_json;
    if (!wj) return null;
    // V2 JSON has either estimated_duration_minutes at top level, or
    // we can sum day.duration_min across non-rest days.
    if (typeof wj === 'object' && wj.estimated_duration_minutes) {
      return wj.estimated_duration_minutes;
    }
    if (typeof wj === 'object' && Array.isArray(wj.days)) {
      const sum = wj.days.reduce((acc, d) => acc + (d.duration_min || 0), 0);
      return sum > 0 ? sum : null;
    }
    return null;
  };
  const formatHM = (min) => {
    if (!min || min <= 0) return '—';
    if (min < 60) return `${min} דק'`;
    const h = Math.floor(min / 60);
    const m = Math.round(min % 60);
    return `${h}:${String(m).padStart(2, '0')}`;
  };
  const weeklyDurationMin = computeWeeklyDurationMin(plan);

  return (
    <div className="min-h-screen bg-bg">
    <div className="max-w-lg mx-auto" dir="rtl">

      <Header
        title="תכנית"
        subtitle={headerSubtitle}
        onBack={onBack}
      />

      {/* 2026-10-30: a real profile change the stored weeks do not reflect yet — never shown as up to date */}
      <PlanRebuildStatus userId={userId} />

      {/* State 2 — non-blocking 'syncing with coach' banner. Renders whenever at least one
          background generation is in flight. Stays out of the user's way: thin, above the tabs,
          no interaction required. The bar fades after the last task finishes (activeCount → 0). */}
      {bgSync.activeCount > 0 && (
        <div className="bg-accent-soft border-y border-border px-4 py-2">
          <div className="max-w-lg mx-auto flex items-center justify-center gap-2 text-xs font-medium text-accent">
            <ArrowsClockwise size={14} weight="bold" />
            <span>מסנכרן עם המאמן — {bgSync.activeCount} {bgSync.activeCount === 1 ? 'תכנית בעבודה' : 'תכניות בעבודה'}…</span>
          </div>
        </div>
      )}

      {/* P0 2026-10-18: a week that could not be generated automatically — the reason and a manual retry
          (never an automatic loop). */}
      {autoRegenBlocked.length > 0 && bgSync.activeCount === 0 && (
        <div className="bg-pressed border-y border-border px-4 py-2" role="alert">
          <div className="max-w-lg mx-auto flex items-center justify-between gap-2 text-xs">
            <span className="text-text-body">
              {autoRegenBlocked.map(b => `${WEEK_LABEL(b.week)}: ${BLOCK_REASON(b.code)}`).join(' · ')}
            </span>
            <button type="button" onClick={manualRetry} disabled={manualRetrying}
                    className="shrink-0 px-2 py-1 rounded border border-border text-text-body disabled:opacity-50">
              {manualRetrying ? 'מנסה…' : 'נסה שוב'}
            </button>
          </div>
        </div>
      )}

      {/* State 2 — transient toast for completed 'next week' plans. Auto-dismisses after 3
          seconds via the dedicated useEffect. Fixed-position so it floats above the dashboard
          without shifting the layout. */}
      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-30 max-w-md px-5 py-3 rounded-control bg-positive-soft border border-positive shadow-card text-positive text-sm font-semibold">
          {toast}
        </div>
      )}

      <main className="px-4 py-6">
        <div className="max-w-lg mx-auto flex flex-col gap-4">

          {/* Sport tabs — only when there is a second sport to switch to (cycling is hidden while CYCLING_ENABLED
              is off, and a one-option switch is noise) */}
          {CYCLING_ENABLED && (
          <div className="flex bg-surface rounded-control p-1 border border-border">
            <button
              type="button"
              onClick={() => userSports.includes('running') && handleSportTabChange('running')}
              disabled={!userSports.includes('running')}
              title={!userSports.includes('running') ? 'לא נבחר ענף ריצה בפרופיל' : ''}
              className={`flex-1 py-3 px-4 rounded-[8px] font-bold text-sm flex items-center justify-center gap-2 ${
                !userSports.includes('running')
                  ? 'opacity-30 cursor-not-allowed text-text-muted'
                  : sportTab === 'running'
                    ? 'bg-accent text-on-accent'
                    : 'text-text-muted'
              }`}
            >
              <PersonSimpleRun weight="bold" size={17} />
              <span>ריצה</span>
            </button>
            {CYCLING_ENABLED && (
              <button
                type="button"
                onClick={() => userSports.includes('cycling') && handleSportTabChange('cycling')}
                disabled={!userSports.includes('cycling')}
                title={!userSports.includes('cycling') ? 'לא נבחר ענף רכיבה בפרופיל' : ''}
                className={`flex-1 py-3 px-4 rounded-[8px] font-bold text-sm flex items-center justify-center gap-2 ${
                  !userSports.includes('cycling')
                    ? 'opacity-30 cursor-not-allowed text-text-muted'
                    : sportTab === 'cycling'
                      ? 'bg-accent text-on-accent'
                      : 'text-text-muted'
                }`}
              >
                <Bicycle weight="bold" size={17} />
                <span>רכיבה</span>
              </button>
            )}
          </div>
          )}

          {/* Generate error */}
          {generateError && (
            generateError === 'limit_reached' ? (
              <Card className="text-center">
                <div className="w-12 h-12 rounded-control bg-accent-soft text-accent flex items-center justify-center mx-auto mb-3">
                  <HourglassHigh size={22} weight="bold" />
                </div>
                <p className="font-bold text-[15px] text-text mb-1">הגעת למגבלת ההודעות היומית</p>
                <div className="flex gap-3 justify-center mb-4 mt-3">
                  {limitData?.dailyLimit > 0 && (
                    <div className="flex-1 bg-surface-2 rounded-control p-3">
                      <p className="font-black text-2xl text-text tabular-nums">{limitData.dailyLimit}</p>
                      <p className="text-text-muted text-xs mt-0.5">הודעות מחר</p>
                    </div>
                  )}
                  {limitData?.trialDaysLeft > 0 && (
                    <div className="flex-1 bg-surface-2 rounded-control p-3">
                      <p className="font-black text-2xl text-text tabular-nums">{limitData.trialDaysLeft}</p>
                      <p className="text-text-muted text-xs mt-0.5">ימים בניסיון</p>
                    </div>
                  )}
                </div>
                <p className="text-text-muted text-xs mb-1">החידוש הבא בעוד</p>
                <p className="font-black text-2xl text-accent tabular-nums mb-4">{countdown}</p>
                <div className="px-4 py-2.5 rounded-control bg-accent-soft text-accent text-sm font-bold mb-3">
                  נתראה מחר
                </div>
                <button type="button" onClick={() => { setGenerateError(null); setLimitData(null); }} className="text-xs text-text-muted">
                  סגור
                </button>
              </Card>
            ) : (
              <Card className="text-center" accentSide="quality">
                <p className="text-quality text-sm">{generateError}</p>
                <button type="button" onClick={() => setGenerateError(null)} className="mt-3 text-xs text-text-muted">
                  סגור
                </button>
              </Card>
            )
          )}

          {/* Loading state — skeletons in the shape of the content (§4.9), no spinner */}
          {isLoading && (
            <div className="flex flex-col gap-3">
              <Skeleton rounded="card" className="h-20" />
              <Skeleton rounded="card" className="h-40" />
              <Skeleton rounded="card" className="h-16" />
            </div>
          )}

          {/* Generating state — one-time transient state, same restraint as Skeleton's loading
              indicator (no bounce/spin loop) */}
          {isGenerating && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
              <div className="w-16 h-16 rounded-card bg-accent-soft text-accent flex items-center justify-center">
                <Sparkle size={28} weight="bold" />
              </div>
              <p className="font-semibold text-[15px] text-text">מכין את התכנית שלך…</p>
              <p className="text-sm text-text-muted">זה עשוי לקחת כמה שניות</p>
            </div>
          )}

          {/* Error state */}
          {error && !isLoading && !isGenerating && (
            <EmptyState
              icon={<SmileySad weight="bold" />}
              message={error}
              action={{ label: 'נסה שוב', onClick: () => fetchAllPlans() }}
            />
          )}

          {/* No plan — empty state. The manual 'צור תכנית' button is admin-only while the
              auto-generation flow (State 1 onboarding loader, State 2 dashboard auto-check) is
              still being stabilised. Regular users see only a 'plan is on its way / contact
              support' message; admin sees the full manual button for testing. */}
          {!isLoading && !isGenerating && !error && !plan && (
            <Card className="text-center">
              <div className="w-16 h-16 rounded-card bg-accent-soft text-accent flex items-center justify-center mx-auto mb-4">
                {sportTab === 'cycling' ? <Bicycle size={28} weight="bold" /> : <PersonSimpleRun size={28} weight="bold" />}
              </div>
              <h2 className="font-bold text-lg text-text mb-2">
                {(() => {
                  const wl = { current: 'שבוע הנוכחי', next: 'שבוע הבא', week_plus2: 'שבוע +2', week_plus3: 'שבוע +3' };
                  return `אין תכנית ${sportTab === 'cycling' ? 'רכיבה' : 'ריצה'} ל${wl[weekTab] || 'שבוע הנוכחי'}`;
                })()}
              </h2>

              {isAdmin ? (
                <>
                  <p className="text-text-muted text-sm mb-6">
                    יצירה ידנית — זמינה למנהלי מערכת בלבד.
                  </p>
                  <Button
                    fullWidth={false}
                    className="px-8"
                    disabled={isGenerating}
                    icon={<MagicWand size={16} weight="bold" />}
                    onClick={() => generatePlan(weekTab, sportTab)}
                  >
                    {isGenerating ? 'יוצר תכנית…' : `צור תכנית ${sportTab === 'cycling' ? 'רכיבה' : 'ריצה'}`}
                  </Button>
                  <p className="text-text-muted text-xs mt-4">
                    נספר כהודעה אחת מהמכסה היומית
                  </p>
                </>
              ) : (
                <>
                  <p className="text-text-body text-sm mb-2">
                    התכנית הזו נוצרת אוטומטית כשנכנסים לאפליקציה.
                  </p>
                  <p className="text-text-muted text-xs mb-6">
                    אם ההודעה נשארת זמן ממושך — רענן את הדף, ואם זה נמשך פנה אלינו: support@ritmoiq.com
                  </p>
                </>
              )}

              {/* Chat fallback — available to everyone */}
              <div className="mt-2 pt-5 border-t border-divider">
                <p className="text-text-muted text-sm mb-3">או</p>
                <Button variant="secondary" fullWidth={false} className="px-6" onClick={onBack}>
                  חזרה למסך הראשי
                </Button>
              </div>
            </Card>
          )}

          {/* Plan display */}
          {!isLoading && !isGenerating && !error && plan && (
            <>
              {isV2Plan(plan.workouts_json) ? (
                // v2 (JSON) — render with the component-driven UI. Pass all four week slots so
                // the renderer shows the week rail.
                <WeeklyPlanRenderer
                  weeks={[
                    // §5.2: a missing week renders as a dashed 'טרם נוצר' chip — never dropped
                    // silently and never shown as '0 ק"מ'.
                    currentSportPlans.current && isV2Plan(currentSportPlans.current.workouts_json)
                      ? { ...currentSportPlans.current, _label: 'current' } : { _label: 'current', _missing: true },
                    currentSportPlans.next && isV2Plan(currentSportPlans.next.workouts_json)
                      ? { ...currentSportPlans.next, _label: 'next' } : { _label: 'next', _missing: true },
                    currentSportPlans.week_plus2 && isV2Plan(currentSportPlans.week_plus2.workouts_json)
                      ? { ...currentSportPlans.week_plus2, _label: 'week_plus2' } : { _label: 'week_plus2', _missing: true },
                    currentSportPlans.week_plus3 && isV2Plan(currentSportPlans.week_plus3.workouts_json)
                      ? { ...currentSportPlans.week_plus3, _label: 'week_plus3' } : { _label: 'week_plus3', _missing: true },
                  ]}
                  initialWeekIdx={0}
                  onWeekChange={(idx) => {
                    const labels = ['current', 'next', 'week_plus2', 'week_plus3'];
                    handleWeekTabChange(labels[idx] || 'current');
                  }}
                />
              ) : plan.workouts_json && plan.workouts_json.plan_text ? (
                // v1 (markdown) — backwards-compat for existing saved plans. Only the outer frame
                // moved onto tokens; the day-by-day markdown formatting below is untouched legacy
                // rendering (out of scope — not part of the plan-a4 reference, which covers v2
                // plans only).
                <Card className="p-0 overflow-hidden">
                  <div className="p-5 text-text-body text-sm leading-relaxed">
                    {formatPlanText(plan.workouts_json.plan_text, sportTab === 'cycling')}
                  </div>
                </Card>
              ) : (
                <EmptyState icon={<CalendarBlank weight="bold" />} message="לא נמצאו אימונים בתכנית" />
              )}

              <p className="text-center text-[13px] text-text-muted leading-relaxed px-2">
                מוצגים 4 שבועות קדימה. שינוי קצבים — בהמלצת המאמן ובאישורך, בצ׳אט.
              </p>

              {/* Admin-only manual regenerate — regular users get no button here; generation is
                  automatic (State 1 / State 2). The header's 'עדכן' action covers manual refresh
                  for everyone. */}
              {isAdmin && (
                <>
                  <div className="flex justify-end">
                    <Button
                      variant="secondary"
                      fullWidth={false}
                      disabled={isGenerating}
                      icon={<MagicWand size={15} weight="bold" />}
                      onClick={() => generatePlan(weekTab, sportTab)}
                      title="למנהלים בלבד — צור תכנית מחדש לבדיקות"
                    >
                      {isGenerating ? 'יוצר…' : 'צור תכנית חדשה'}
                    </Button>
                  </div>
                  <p className="text-center text-quality text-[11px]">
                    למנהלים בלבד: יצירה ידנית תחליף את התכנית הקיימת ותספר כהודעה
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </main>
    </div>
    </div>
  );
}

export default WeeklyPlan;
